import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { UseIndicatorProps } from './props';

/**
 * Custom hook to manage the position and size of an indicator element
 * based on the currently selected tab.
 *
 * @template T - The type of the tab value.
 * @param {UseIndicatorProps<T>} props - The properties for the indicator hook.
 * @param {Array<{ value: T }>} props.tabs - An array of tab objects, each containing a value.
 * @param {boolean} props.isDisabled - A flag to disable the indicator adjustment.
 * @param {T} props.currentTab - The current active tab value.
 * @param {MutableRefObject<HTMLDivElement>} props.containerRef - A ref to the container element holding the tabs.
 *
 * @returns {MutableRefObject<HTMLDivElement>} - A ref to the indicator element.
 *
 * Example usage:
 * const indicatorRef = useIndicator({
 *   tabs: [{ value: 'tab1' }, { value: 'tab2' }],
 *   isDisabled: false,
 *   currentTab: 'tab1',
 *   containerRef: someContainerRef,
 * });
 */
export function useIndicator<T>({
  tabs,
  isDisabled,
  currentTab,
  containerRef,
}: UseIndicatorProps<T>): MutableRefObject<HTMLDivElement> {
  // Calculate the position of the current tab using useMemo to optimize performance
  const position = useMemo(
    () => tabs.findIndex(tab => tab.value == currentTab),
    [tabs, currentTab],
  );

  // Create a ref for the indicator element
  const indicatorRef: MutableRefObject<HTMLDivElement> =
    useRef<HTMLDivElement>(null);

  /**
   * Adjusts the indicator's position and size based on the current tab.
   * Uses useCallback to memoize the function and prevent unnecessary re-renders.
   */
  const handleAdjustIndicator = useCallback(() => {
    if (!indicatorRef.current || !containerRef.current || isDisabled) return;

    const tabsContainer = containerRef.current;
    const indicatorEl = indicatorRef.current;
    const tabButtons = tabsContainer.querySelectorAll('button');

    if (!tabButtons?.[position]) {
      indicatorEl.style.width = '0px';
      return;
    }

    const tabEl = tabButtons[position];
    const left = tabEl.offsetLeft;
    const width = tabEl.offsetWidth;

    indicatorEl.style.width = `${width}px`;
    indicatorEl.style.transform = `translateX(${left}px)`;
  }, [position, isDisabled, containerRef]);

  /**
   * Sets up the event listener for window resize to adjust the indicator.
   * Returns a cleanup function to remove the event listener.
   */
  function onMount() {
    handleAdjustIndicator();

    window.addEventListener('resize', handleAdjustIndicator);

    return () => {
      window.removeEventListener('resize', handleAdjustIndicator);
    };
  }

  // Use the useEffect hook to add the resize event listener when the component mounts and clean up when it unmounts
  useEffect(onMount, [handleAdjustIndicator]);

  // Return the ref to the indicator element
  return indicatorRef;
}

export type { UseIndicatorProps };
